/**
 * Sprint 2b loss-aversion ad-hook actions: panic exit (sell at the pre-crash
 * price), rug insurance activation, and trade rewind (undo the last trade).
 * All are dispatched AFTER a rewarded ad completes (UI owns the ad flow and
 * the adHookStats logging).
 */
import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import { getCoinConfig } from './config/coins.js';
import { INSURANCE_CONFIG, PANIC_CONFIG, REWIND_CONFIG, RUG_CONFIG } from './config/risk.js';
import type { LastTradeState, RugPullState } from './riskState.js';

const EPSILON = 1e-9;

export type RiskAction =
  | { type: 'PANIC_SELL'; payload: { coinId: string } }
  | { type: 'BUY_RUG_INSURANCE'; payload: { coinId: string } }
  | { type: 'TRADE_REWIND' };

export interface PanicQuote {
  refPrice: number;
  sellAmount: number;
  proceeds: number;
}

/** Price ~PANIC_CONFIG.refWindow ago from the rolling samples (null if none). */
function samplePriceBefore(state: GameState, coinId: string, now: number): number | null {
  const samples = state.risk?.samples[coinId] ?? [];
  const cutoff = now - PANIC_CONFIG.refWindow;
  const eligible = samples.filter(s => s.t <= cutoff);
  const ref = eligible.length > 0 ? eligible[eligible.length - 1] : samples[0];
  return ref ? ref.p : null;
}

/**
 * Rug-path panic window: the crash itself plus an ad-length grace into the
 * floor phase (crashes last 10~20s - shorter than a rewarded ad).
 */
function inRugPanicWindow(rug: RugPullState | undefined, now: number): boolean {
  if (!rug || rug.panicUsed) return false;
  if (rug.phase === 'crashing') return true;
  return rug.phase === 'floor'
    && now - rug.startedAt <= RUG_CONFIG.crashMax + PANIC_CONFIG.rugGraceAfterCrash;
}

/**
 * Panic exit eligibility + quote. Eligible while a rug is crashing (ref =
 * pre-crash price) or when the coin dropped >=25% within 30s (ref = price
 * 30s ago). Null when not eligible.
 */
export function getPanicQuote(state: GameState, coinId: string, now: number): PanicQuote | null {
  const holding = state.exchange?.holdings[coinId];
  const coin = state.exchange?.coins[coinId];
  if (!state.risk || !holding || !coin || holding.amount <= 0) return null;
  if (now < (state.risk.panicLockUntil[coinId] ?? 0)) return null;

  const rug = state.risk.rugs[coinId];
  let refPrice: number | null = null;
  if (inRugPanicWindow(rug, now)) {
    refPrice = rug!.startPrice;
  } else if (!rug || rug.phase === 'none') {
    const ref = samplePriceBefore(state, coinId, now);
    if (ref !== null && ref > 0 && ((ref - coin.price) / ref) * 100 >= PANIC_CONFIG.crashDropPct) {
      refPrice = ref;
    }
  }
  if (refPrice === null || refPrice <= 0) return null;

  const sellAmount = holding.amount * PANIC_CONFIG.sellRatio;
  return { refPrice, sellAmount, proceeds: sellAmount * refPrice };
}

/** Panic exit: sell 50% of the holding at the pre-crash price (ad reward). */
export const handlePanicSell: ActionHandler<
  GameState,
  Extract<RiskAction, { type: 'PANIC_SELL' }>
> = (state, action) => {
  const { coinId } = action.payload;
  const now = Date.now();
  const quote = getPanicQuote(state, coinId, now);
  if (!quote || !state.exchange || !state.risk) return null;

  const holding = state.exchange.holdings[coinId];
  const remaining = holding.amount - quote.sellAmount;
  const holdings = { ...state.exchange.holdings };
  if (remaining <= EPSILON) delete holdings[coinId];
  else holdings[coinId] = { amount: remaining, avgPrice: holding.avgPrice };

  const rug = state.risk.rugs[coinId];
  const rugs = rug && rug.phase !== 'none'
    ? { ...state.risk.rugs, [coinId]: { ...rug, panicUsed: true } }
    : state.risk.rugs;

  return {
    ...state,
    coins: state.coins + quote.proceeds,
    exchange: {
      ...state.exchange,
      holdings,
      totalTrades: state.exchange.totalTrades + 1,
      realizedProfit: state.exchange.realizedProfit
        + (quote.refPrice - holding.avgPrice) * quote.sellAmount,
    },
    risk: {
      ...state.risk,
      rugs,
      panicLockUntil: { ...state.risk.panicLockUntil, [coinId]: now + PANIC_CONFIG.cooldown },
    },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

/** Rug insurance: 30 min policy on one meme coin (ad reward, 1 per coin). */
export const handleBuyRugInsurance: ActionHandler<
  GameState,
  Extract<RiskAction, { type: 'BUY_RUG_INSURANCE' }>
> = (state, action) => {
  const { coinId } = action.payload;
  const cfg = getCoinConfig(coinId);
  if (!state.risk || !cfg || cfg.tier !== 'meme') return null;
  const now = Date.now();
  const existing = state.risk.insurance[coinId];
  if (existing && now < existing.expiresAt) return null;

  return {
    ...state,
    risk: {
      ...state.risk,
      insurance: {
        ...state.risk.insurance,
        [coinId]: { activatedAt: now, expiresAt: now + INSURANCE_CONFIG.duration },
      },
    },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

export interface RewindQuote {
  trade: LastTradeState;
  adversePct: number;
  /** true while the hook should be visible (60s window) */
  withinDisplay: boolean;
}

/** Trade rewind eligibility: last trade moved >=8% against the fill price. */
export function getRewindQuote(state: GameState, now: number): RewindQuote | null {
  const trade = state.risk?.lastTrade;
  if (!state.risk || !trade || trade.price <= 0) return null;
  if (now < state.risk.rewindReadyAt) return null;
  if (now - trade.at > REWIND_CONFIG.executeWindow) return null;
  const coin = state.exchange?.coins[trade.coinId];
  if (!coin) return null;

  const movePct = ((coin.price - trade.price) / trade.price) * 100;
  const adversePct = trade.side === 'buy' ? -movePct : movePct;
  if (adversePct < REWIND_CONFIG.adversePct) return null;
  // A sell rewind pays the proceeds back - must still have the cash
  if (trade.side === 'sell' && state.coins + EPSILON < trade.cash) return null;

  return { trade, adversePct, withinDisplay: now - trade.at <= REWIND_CONFIG.window };
}

/** Trade rewind: cancel the last trade, restoring cash + holding (ad reward). */
export const handleTradeRewind: ActionHandler<
  GameState,
  Extract<RiskAction, { type: 'TRADE_REWIND' }>
> = (state) => {
  const now = Date.now();
  const quote = getRewindQuote(state, now);
  if (!quote || !state.exchange || !state.risk) return null;
  const trade = quote.trade;

  const holdings = { ...state.exchange.holdings };
  if (trade.prevHolding) holdings[trade.coinId] = trade.prevHolding;
  else delete holdings[trade.coinId];

  return {
    ...state,
    coins: trade.side === 'buy' ? state.coins + trade.cash : state.coins - trade.cash,
    exchange: {
      ...state.exchange,
      holdings,
      totalTrades: Math.max(0, state.exchange.totalTrades - 1),
      realizedProfit: state.exchange.realizedProfit - trade.realizedProfit,
    },
    risk: { ...state.risk, lastTrade: null, rewindReadyAt: now + REWIND_CONFIG.cooldown },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

export const riskActionHandlers = {
  PANIC_SELL: handlePanicSell,
  BUY_RUG_INSURANCE: handleBuyRugInsurance,
  TRADE_REWIND: handleTradeRewind,
} as const;
